import React, { ReactNode } from "react"
import {
  Box, Flex, FlexProps, Stack, Text, forwardRef,
} from "@chakra-ui/react"
import { GuestSummary, SeatingAssignment } from "sharedTypes"
import GuestAvatar from "components/elements/GuestAvatar"
import Icon from "components/elements/Icon"
import { LAST_NAME_ORDER_PREFIX } from "utilities/constants"
import GroupLabel from "./GroupLabel"

type RowProps = {
  children: ReactNode
  selected?: boolean
  /** Greyed out, in lists that show where guests sit */
  seated?: boolean
  dragging?: boolean
}

/** A guest in a list, the look only: each list adds what a click or a drag does. */
export const GuestRow = ({
  children, selected, seated, dragging,
}: RowProps) => {
  let bg = "none"
  if (seated) {
    bg = "gray.200"
  } else if (selected) {
    bg = "blue.50"
  }

  return (
    <Flex position="relative" borderBottomWidth={1} bg={bg} opacity={dragging ? 0.5 : 1}>
      {children}
    </Flex>
  )
}

type BodyProps = FlexProps & {
  plusOne?: boolean
  disabled?: boolean
}

/** The row's main area, where a click or a drag lands: a link, a box… */
export const GuestRowBody = forwardRef<BodyProps, "div">(({ plusOne, disabled, ...props }, ref) => (
  <Flex
    ref={ref}
    align="center"
    flexGrow={1}
    minW={0}
    pl={plusOne ? 20 : 8}
    pr={4}
    py={3}
    opacity={disabled ? 0.3 : undefined}
    {...props}
  />
))

type PersonProps = {
  guest: GuestSummary
  /** The list's order, the name reads last name first in a last name order */
  order?: string
}

/** The guest's avatar, name and group. */
export const GuestRowPerson = forwardRef<PersonProps, "div">(({ guest, order }, ref) => {
  const {
    personInfo: { firstName, lastName, photoUrl }, status, checkedIn, groupSize, plusOnes,
  } = guest
  const lastNameFirst = order?.startsWith(LAST_NAME_ORDER_PREFIX)

  return (
    <Flex align="center" mr={3} ref={ref}>
      <Box mt={1}>
        <GuestAvatar
          firstName={firstName}
          lastName={lastName}
          photoUrl={photoUrl}
          status={status}
          checkedIn={!!checkedIn}
        />
      </Box>
      <Flex align="center" p={3} wrap="wrap">
        <Box mr={3}>
          <Text as="span" fontWeight="bold">{lastNameFirst ? lastName : firstName}</Text>
          {lastNameFirst && lastName ? ", " : " "}
          {lastNameFirst ? firstName : lastName}
        </Box>
        {groupSize > 1 && (
          <GroupLabel groupSize={groupSize} childGuest={!plusOnes?.length} />
        )}
      </Flex>
    </Flex>
  )
})

/** Where the guest sits, at the row's end. */
export const GuestRowSeat = ({
  seatingTableTitle, seat, shape,
}: Pick<NonNullable<SeatingAssignment>, "seatingTableTitle" | "seat" | "shape">) => (
  <Stack
    borderLeftWidth={1}
    flexShrink={0}
    px={2}
    py={2}
    w={16}
    textAlign="center"
    spacing={0}
  >
    <Icon icon={`table-${shape}`} size={8} m="auto" />
    <Text fontSize="xs" lineHeight={1} noOfLines={1}>{seatingTableTitle}</Text>
    <Text fontSize="xs" lineHeight={1} noOfLines={1}>Seat {seat}</Text>
  </Stack>
)
